import * as React from 'react';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { DataGrid, GridColDef, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import SwapVertIcon from '@mui/icons-material/SwapVert';

import dayjs from 'dayjs';

import useAxios from '../../utils/useAxios';
import { SectionCard, EmptyState, number } from '../ui';
import { TRUE_COLORS } from '../../theme';

const MovementsTab = ({ locations }: { locations: any[] }) => {
  const axios = useAxios();
  const [logs, setLogs] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [location, setLocation] = React.useState('all');
  // Every sale adds rows here, so the list loads one period at a time.
  const [days, setDays] = React.useState(30);

  React.useEffect(() => {
    setLoading(true);
    const params: Record<string, string | number> = {
      compact: 1,
      start_date: dayjs().subtract(days, 'day').startOf('day').utc().format(),
      end_date: dayjs().endOf('day').utc().format(),
    };
    if (location !== 'all') params.location = location;
    axios.get('inventory/inventory-log-history/', { params })
      .then(r => setLogs(r.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [axios, location, days]);

  const columns: GridColDef[] = [
    {
      field: 'effective_date', headerName: 'Fecha', flex: 0.8, minWidth: 160, type: 'dateTime',
      valueGetter: v => (v ? new Date(v) : null),
      valueFormatter: (v: Date | null) => (v ? dayjs(v).format('D MMM YYYY, HH:mm') : '—'),
    },
    { field: 'product', headerName: 'Producto', flex: 1.4, minWidth: 200, valueGetter: (_v, row: any) => row.inventory_item?.product?.name ?? '' },
    { field: 'location', headerName: 'Sucursal', flex: 0.8, minWidth: 140, valueGetter: (_v, row: any) => row.inventory_item?.location?.name ?? '' },
    {
      field: 'change', headerName: 'Cambio', type: 'number', flex: 0.5, minWidth: 100,
      renderCell: ({ value }) => (
        <Box component="span" sx={{ fontWeight: 700, color: value > 0 ? TRUE_COLORS.success : value < 0 ? TRUE_COLORS.danger : 'text.secondary' }}>
          {value > 0 ? '+' : ''}{number(value, 2)}
        </Box>
      ),
    },
    // Older TRUE POS versions logged manual adjustments in English.
    { field: 'reason', headerName: 'Motivo', flex: 1.2, minWidth: 180, valueFormatter: v => (v === 'Manual change.' ? 'Ajuste manual' : v || '—') },
  ];

  return (
    <SectionCard noPadding>
      <DataGrid
        rows={logs}
        columns={columns}
        loading={loading}
        autoHeight
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        slots={{
          toolbar: () => (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ px: 2, pt: 2, pb: 1, alignItems: { md: 'center' } }}>
              <GridToolbarQuickFilter placeholder="Buscar producto o motivo…" debounceMs={200} sx={{ width: { xs: '100%', md: 300 } }} />
              <Box sx={{ flexGrow: 1 }} />
              <TextField select size="small" label="Periodo" value={days} onChange={e => setDays(Number(e.target.value))} sx={{ minWidth: 160 }}>
                <MenuItem value={7}>Últimos 7 días</MenuItem>
                <MenuItem value={30}>Últimos 30 días</MenuItem>
                <MenuItem value={90}>Últimos 90 días</MenuItem>
              </TextField>
              <TextField select size="small" label="Sucursal" value={location} onChange={e => setLocation(e.target.value)} sx={{ minWidth: 180 }}>
                <MenuItem value="all">Todas</MenuItem>
                {locations.map(l => <MenuItem key={l.id} value={String(l.id)}>{l.name}</MenuItem>)}
              </TextField>
            </Stack>
          ),
          noRowsOverlay: () => (
            <EmptyState compact icon={<SwapVertIcon />} title="Sin movimientos" description="Entradas, salidas, conteos y órdenes recibidas aparecerán aquí." />
          ),
        }}
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        sx={{ '--DataGrid-overlayHeight': '220px' }}
      />
    </SectionCard>
  );
};

export default MovementsTab;
